import { get, del, post } from '@/lib/api/client'
import type {
  ApiResponse,
} from '@/lib/api/types'

// Reply type
export interface Reply {
  id: string
  pressReleaseId: string
  responderName: string
  responderEmail: string
  response: string // API returns 'response' but UI might expect 'reply'. The user's JSON shows 'response' in the reply object, but 'reply' in the POST payload.
  dateReplied: string
}

// Comment type with nested replies
export interface Comment {
  id: string
  pressReleaseId: string
  name: string
  email: string
  comment: string
  dateCommented: string
  replyCount?: number
}

// Comment with replies structure as per "pressReleaseReplyResponseDetails"
export interface CommentWithReplies {
  pressReleaseCommentResponse: Comment
  pressReleaseReplyResponses: Reply[]
}

// The outer wrapper provided in the "data" array
export interface RepliesWrapper {
  pressReleaseReplyResponseDetails: CommentWithReplies[]
}

export class PressReleaseCommentService {
  /**
   * Get comments with replies for a press release
   * GET /press-release-replies/public/{pressReleaseId}/all
   */
  async getCommentsWithReplies(
    pressReleaseId: string,
  ): Promise<ApiResponse<RepliesWrapper[]>> {
    return await get<ApiResponse<RepliesWrapper[]>>(
      `press-release-replies/public/${pressReleaseId}/all`
    )
  }

  /**
   * Add a reply to a comment
   * POST /press-release-replies/private/addreply/{commentId}
   */
  async addReply(commentId: string, reply: string): Promise<ApiResponse<void>> {
    return await post<ApiResponse<void>>(
      `press-release-replies/private/addreply/${commentId}`,
      { reply }
    )
  }

  /**
   * Delete a single reply
   * DELETE /press-release-replies/public/reply/{replyId}
   */
  async deleteReply(replyId: string): Promise<ApiResponse<void>> {
    return await del<ApiResponse<void>>(
      `press-release-replies/public/reply/${replyId}`
    )
  }

  /**
   * Delete a comment and all its replies
   * DELETE /press-release-replies/public/comments/{commentId}/replies
   */
  async deleteCommentWithReplies(commentId: string): Promise<ApiResponse<void>> {
    return await del<ApiResponse<void>>(
      `press-release-replies/public/comments/${commentId}/replies`
    )
  }
}

// Export singleton instance
export const pressReleaseCommentService = new PressReleaseCommentService()

// Export default
export default pressReleaseCommentService

